import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryOpts } from "~api/helpers.ts";
import { tenantKeys } from "~api/tenants.ts";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext.tsx";
import type {
  PhotoDocConstructionSite,
  PhotoDocFullImage,
  PhotoDocHistoryEvent,
  PhotoDocManagerAccount,
  PhotoDocPhotosResponse,
  PhotoDocRecentPhoto,
  PhotoDocWorkPackageWithCount,
} from "~generated";
import { PhotoDocumentationService } from "~generated";
import { usePhotoDocAnalytics } from "../photoDocAnalytics.ts";
import { pollWhilePending } from "./pollWhilePending.ts";

interface PhotoFilters {
  startDate?: string;
  endDate?: string;
}

export const photoDocViewKeys = {
  base: (tenantId: string) =>
    [
      ...tenantKeys.detail(tenantId),
      "photo_documentation",
      "view",
      "v1",
    ] as const,
  recent: (tenantId: string, limit?: number) =>
    [...photoDocViewKeys.base(tenantId), "recent", limit ?? null] as const,
  constructionSites: (tenantId: string, managerId?: string) =>
    [
      ...photoDocViewKeys.base(tenantId),
      "construction_sites",
      managerId ?? null,
    ] as const,
  workPackages: (tenantId: string, siteId: string) =>
    [
      ...photoDocViewKeys.base(tenantId),
      "construction_site",
      siteId,
      "work_packages",
    ] as const,
  photos: (tenantId: string, wpId: string, filters?: PhotoFilters) =>
    [
      ...photoDocViewKeys.base(tenantId),
      "work_package",
      wpId,
      "photos",
      filters,
    ] as const,
  managerAccounts: (tenantId: string, managerId?: string) =>
    [
      ...photoDocViewKeys.base(tenantId),
      "manager_accounts",
      managerId ?? null,
    ] as const,

  photoFullImage: (tenantId: string, photoId: string) =>
    [...photoDocViewKeys.base(tenantId), "photo", photoId, "full"] as const,

  history: (tenantId: string, photoDocId: string) =>
    [
      ...photoDocViewKeys.base(tenantId),
      "photo_doc",
      photoDocId,
      "history",
    ] as const,
};

export function useRecentPhotos(
  options?: QueryOpts<
    PhotoDocRecentPhoto[],
    ReturnType<typeof photoDocViewKeys.recent>
  > & { limit?: number },
) {
  const currentTenant = useCurrentTenant();
  const { limit, ...queryOptions } = options ?? {};

  return useQuery({
    refetchInterval: (query) => pollWhilePending(query.state.data),
    ...queryOptions,
    queryKey: photoDocViewKeys.recent(currentTenant.id, limit),
    queryFn: async () => {
      const data =
        await PhotoDocumentationService.getApiTenantsResourcesPhotoDocumentationMyRecentPhotos(
          currentTenant.id,
          limit,
        );
      return data.photos;
    },
  });
}

/**
 * Fetches construction sites with photo documentations, filtered by employee role
 * and optionally by the construction-site-manager id.
 */
export function useConstructionSitesWithPhotos(
  managerId?: string,
  options?: QueryOpts<
    PhotoDocConstructionSite[],
    ReturnType<typeof photoDocViewKeys.constructionSites>
  >,
) {
  const currentTenant = useCurrentTenant();

  return useQuery({
    ...options,
    queryKey: photoDocViewKeys.constructionSites(currentTenant.id, managerId),
    queryFn: async () => {
      const data =
        await PhotoDocumentationService.getApiTenantsResourcesPhotoDocumentationConstructionSites(
          currentTenant.id,
          managerId,
        );
      return data.construction_sites;
    },
  });
}

/**
 * Fetches work packages with photo docs for a specific construction site
 * Designed for lazy loading when expanding a construction site
 */
export function useWorkPackagesForConstructionSite(
  siteId: string,
  options?: QueryOpts<
    PhotoDocWorkPackageWithCount[],
    ReturnType<typeof photoDocViewKeys.workPackages>
  >,
) {
  const currentTenant = useCurrentTenant();

  return useQuery({
    ...options,
    queryKey: photoDocViewKeys.workPackages(currentTenant.id, siteId),
    queryFn: async () => {
      const data =
        await PhotoDocumentationService.getApiTenantsResourcesPhotoDocumentationConstructionSitesWorkPackages(
          currentTenant.id,
          siteId,
        );
      return data.work_packages;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useManagerAccountPhotos(
  managerId?: string,
  options?: QueryOpts<
    PhotoDocManagerAccount[],
    ReturnType<typeof photoDocViewKeys.managerAccounts>
  >,
) {
  const currentTenant = useCurrentTenant();

  return useQuery({
    refetchInterval: (query) =>
      pollWhilePending(query.state.data?.flatMap((doc) => doc.photos)),
    ...options,
    queryKey: photoDocViewKeys.managerAccounts(currentTenant.id, managerId),
    queryFn: async () => {
      const data =
        await PhotoDocumentationService.getApiTenantsResourcesPhotoDocumentationManagerAccounts(
          currentTenant.id,
          managerId,
        );
      return data.photo_docs;
    },
  });
}

/**
 * Lazy-fetches a presigned URL for the full-resolution photo. Pass
 * `enabled: false` until the drawer/lightbox is actually opened so the
 * larger blob isn't downloaded just because the list rendered.
 */
export function usePhotoFullImage(
  photoId: string,
  options?: QueryOpts<
    PhotoDocFullImage,
    ReturnType<typeof photoDocViewKeys.photoFullImage>
  >,
) {
  const currentTenant = useCurrentTenant();

  return useQuery({
    ...options,
    queryKey: photoDocViewKeys.photoFullImage(currentTenant.id, photoId),
    queryFn: () =>
      PhotoDocumentationService.getApiTenantsResourcesPhotoDocumentationPhotosFull(
        currentTenant.id,
        photoId,
      ),
  });
}

export function usePhotoFullImageUrl({ photoId }: { photoId: string }) {
  const currentTenant = useCurrentTenant();

  return useMutation({
    mutationFn: async () => {
      const { url } =
        await PhotoDocumentationService.getApiTenantsResourcesPhotoDocumentationPhotosFull(
          currentTenant.id,
          photoId,
        );
      if (!url) {
        throw new Error(`Full image URL missing for photo ${photoId}`);
      }
      return url;
    },
  });
}

/**
 * Presigned URL that makes the browser download the photo instead of showing
 * it. Used for the single-photo download; the group download goes through
 * `useDownloadManifest`.
 */
export function usePhotoDownloadUrl({ photoId }: { photoId: string }) {
  const currentTenant = useCurrentTenant();

  return useMutation({
    mutationFn: async () => {
      const { url } =
        await PhotoDocumentationService.getApiTenantsResourcesPhotoDocumentationPhotosFull(
          currentTenant.id,
          photoId,
          "attachment",
        );
      if (!url) {
        throw new Error(`Download URL missing for photo ${photoId}`);
      }
      return url;
    },
  });
}

export function useDownloadManifest() {
  const currentTenant = useCurrentTenant();

  return useMutation({
    meta: { expectedErrorStatuses: [409] },
    mutationFn: (photoDocId: string) =>
      PhotoDocumentationService.getApiTenantsResourcesPhotoDocumentationPhotoDocsDownloadManifest(
        currentTenant.id,
        photoDocId,
      ),
  });
}

export function useDeletePhoto() {
  const currentTenant = useCurrentTenant();
  const queryClient = useQueryClient();
  const { trackPhotoDeleted } = usePhotoDocAnalytics();

  return useMutation({
    mutationFn: (photoId: string) =>
      PhotoDocumentationService.deletePhotoDocPhoto(currentTenant.id, photoId),
    onSuccess: () => {
      trackPhotoDeleted();
      return queryClient.invalidateQueries({
        queryKey: photoDocViewKeys.base(currentTenant.id),
      });
    },
  });
}

export function useDeletePhotoDoc() {
  const currentTenant = useCurrentTenant();
  const queryClient = useQueryClient();
  const { trackPhotoDocDeleted } = usePhotoDocAnalytics();

  return useMutation({
    mutationFn: (photoDocId: string) =>
      PhotoDocumentationService.deletePhotoDoc(currentTenant.id, photoDocId),
    onSuccess: () => {
      trackPhotoDocDeleted();
      return queryClient.invalidateQueries({
        queryKey: photoDocViewKeys.base(currentTenant.id),
      });
    },
  });
}

/**
 * Lazy-fetches the history events of a photo documentation. Pass
 * `enabled: false` until the history dialog is opened so the request only
 * fires on demand.
 */
export function usePhotoDocHistory(
  photoDocId: string,
  options?: QueryOpts<
    PhotoDocHistoryEvent[],
    ReturnType<typeof photoDocViewKeys.history>
  >,
) {
  const currentTenant = useCurrentTenant();

  return useQuery({
    ...options,
    queryKey: photoDocViewKeys.history(currentTenant.id, photoDocId),
    queryFn: async () => {
      const data = await PhotoDocumentationService.getPhotoDocHistory(
        currentTenant.id,
        photoDocId,
      );
      return data.history_events;
    },
  });
}

/**
 * Fetches photos grouped by date for a specific work package
 */
export function useWorkPackagePhotos(
  workPackageId: string,
  options?: QueryOpts<
    PhotoDocPhotosResponse,
    ReturnType<typeof photoDocViewKeys.photos>
  > &
    PhotoFilters,
) {
  const currentTenant = useCurrentTenant();
  const { startDate, endDate, ...queryOptions } = options ?? {};

  const filters: PhotoFilters | undefined =
    startDate || endDate ? { startDate, endDate } : undefined;

  return useQuery({
    refetchInterval: (query) =>
      pollWhilePending(
        query.state.data?.photos_by_date.flatMap((day) =>
          day.photo_docs.flatMap((doc) => doc.photos),
        ),
      ),
    ...queryOptions,
    queryKey: photoDocViewKeys.photos(currentTenant.id, workPackageId, filters),
    queryFn: async () => {
      const data =
        await PhotoDocumentationService.getApiTenantsResourcesPhotoDocumentationWorkPackagesPhotos(
          currentTenant.id,
          workPackageId,
          startDate,
          endDate,
        );
      return data;
    },
  });
}
